import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';

export type CardField = 'cardNumber' | 'cardholderName' | 'expiry' | 'cvc';

export interface CardFormValue {
  cardNumber: string;
  cardholderName: string;
  expireDate: string;
  expireMonth: string;
  expireYear: string;
  cvc: string;
}

/**
 * COPIA del formulario de tarjeta del front del cliente
 * (elhubsvoffice_front/src/app/shared/card-payment-form): las pantallas de
 * pago públicas y las del panel deben verse y comportarse igual. Si se
 * cambia uno, aplicar el mismo cambio en el otro (solo el CSS difiere: aquí
 * se mapean los tokens del front a la paleta del modal de pago del panel).
 *
 * Es un componente CONTROLADO: la pantalla que lo usa sigue siendo dueña de
 * cardNumber/cardholderName/expireDate/cvc (y de su validación y envío);
 * aquí solo se capturan, sanean y muestran. Cada cambio sale por
 * (cardChange) y un reinicio desde la pantalla (p. ej. cardNumber = '') se
 * refleja solo porque los valores entran por @Input.
 *
 * El saneo conserva únicamente el formato (dígitos, agrupación y longitudes).
 * Nunca corrige silenciosamente un valor semánticamente inválido: el usuario
 * debe poder ver lo que escribió y el motivo exacto del error.
 */
@Component({
  selector: 'app-card-payment-form',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './card-payment-form.component.html',
  styleUrls: ['./card-payment-form.component.css'],
})
export class CardPaymentFormComponent {
  /** Prefijo para los id de los campos (debe ser único en la página). */
  @Input() idPrefix = 'card';
  @Input() cardNumber = '';
  @Input() cardholderName = '';
  @Input() expireDate = '';
  @Input() cvc = '';
  @Input() invalidField: CardField | null = null;
  @Input() disabled = false;

  @Output() cardChange = new EventEmitter<CardFormValue>();
  /** El usuario empezó a corregir este campo (para limpiar su error). */
  @Output() fieldEdited = new EventEmitter<CardField>();

  @ViewChild('numberInput') private numberInput?: ElementRef<HTMLInputElement>;
  @ViewChild('nameInput') private nameInput?: ElementRef<HTMLInputElement>;
  @ViewChild('expiryInput') private expiryInput?: ElementRef<HTMLInputElement>;
  @ViewChild('cvcInput') private cvcInput?: ElementRef<HTMLInputElement>;

  activeField: CardField | null = null;
  private readonly editedFields = new Set<CardField>();

  get flipped(): boolean {
    return this.activeField === 'cvc';
  }

  get brand(): 'visa' | 'mastercard' | 'amex' | '' {
    const digits = this.cardNumber.replace(/\D/g, '');
    if (/^4/.test(digits)) return 'visa';
    if (/^(5[1-5]|2[2-7])/.test(digits)) return 'mastercard';
    if (/^3[47]/.test(digits)) return 'amex';
    return '';
  }

  get brandLabel(): string {
    return { visa: 'VISA', mastercard: 'Mastercard', amex: 'AMEX', '': '' }[this.brand];
  }

  get previewNumber(): string {
    const digits = this.cardNumber.replace(/\D/g, '');
    const length = Math.max(16, digits.length);
    return (digits + '•'.repeat(length)).slice(0, length).replace(/(.{4})(?=.)/g, '$1 ');
  }

  get previewName(): string {
    return this.cardholderName.trim().toUpperCase() || 'NOMBRE DEL TITULAR';
  }

  get previewExpiry(): string {
    return this.expireDate || 'MM/AA';
  }

  get previewCvc(): string {
    return this.cvc ? '•'.repeat(this.cvc.length) : '•••';
  }

  /** Lleva el foco (y el scroll) al campo indicado -- usado tras un error. */
  focus(field: CardField): void {
    const refs: Record<CardField, ElementRef<HTMLInputElement> | undefined> = {
      cardNumber: this.numberInput,
      cardholderName: this.nameInput,
      expiry: this.expiryInput,
      cvc: this.cvcInput,
    };
    const el = refs[field]?.nativeElement;
    if (!el) return;
    setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
    });
  }

  setActive(field: CardField | null): void {
    this.activeField = field;
  }

  fieldError(field: CardField): string {
    if (!this.editedFields.has(field) && this.invalidField !== field) return '';

    if (field === 'cardNumber') {
      const digits = this.cardNumber.replace(/\D/g, '');
      if (!digits) return 'Número requerido.';
      if (!/^\d{13,19}$/.test(digits)) return 'Usa entre 13 y 19 dígitos.';
      return '';
    }

    if (field === 'expiry') {
      if (!/^\d{2}\/\d{2}$/.test(this.expireDate)) return 'Usa el formato MM/AA.';
      const [monthValue, yearValue] = this.expireDate.split('/');
      const month = Number(monthValue);
      const year = 2000 + Number(yearValue);
      if (month < 1 || month > 12) return 'Mes inválido (01–12).';
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;
      if (year < currentYear || (year === currentYear && month < currentMonth)) return 'Tarjeta vencida.';
      if (year > currentYear + 20) return 'Año fuera del límite.';
      return '';
    }

    if (field === 'cardholderName') {
      const holder = this.cardholderName.replace(/\s+/g, ' ').trim();
      return /^[\p{L}]+(?: [\p{L}]+)*$/u.test(holder) && holder.length >= 2
        ? ''
        : 'Usa solo letras y espacios.';
    }

    return /^\d{3,4}$/.test(this.cvc) ? '' : 'Usa 3 o 4 dígitos.';
  }

  hasError(field: CardField): boolean {
    return this.fieldError(field) !== '';
  }

  onNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    // Se permiten algunos dígitos adicionales para que un número demasiado
    // largo sea visible y pueda explicarse; la validez sigue siendo 13..19.
    const digits = input.value.replace(/\D/g, '').slice(0, 24);
    const sanitized = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
    input.value = sanitized;
    this.cardNumber = sanitized;
    this.emit('cardNumber');
  }

  onNameInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = input.value.replace(/[^\p{L}\s]/gu, '').replace(/\s{2,}/g, ' ').slice(0, 80);
    input.value = sanitized;
    this.cardholderName = sanitized;
    this.emit('cardholderName');
  }

  onCvcInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = input.value.replace(/\D/g, '').slice(0, 4);
    input.value = sanitized;
    this.cvc = sanitized;
    this.emit('cvc');
  }

  onExpiryInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 4);
    const month = digits.slice(0, 2);
    const year = digits.slice(2, 4);

    const formatted = year.length > 0 ? `${month}/${year}` : month;
    input.value = formatted;
    this.expireDate = formatted;
    this.emit('expiry');
  }

  private emit(field: CardField): void {
    this.editedFields.add(field);
    const [month = '', year = ''] = this.expireDate.split('/');
    this.cardChange.emit({
      cardNumber: this.cardNumber,
      cardholderName: this.cardholderName,
      expireDate: this.expireDate,
      expireMonth: month,
      expireYear: year.length === 2 ? `20${year}` : '',
      cvc: this.cvc,
    });
    this.fieldEdited.emit(field);
  }
}
